import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import TopHeader from "../../Components/TopHeader";
import SearchBar from "../../Components/common/SearchBar";
import OrganisationLayout from "../layout/OrganisationLayout";
import {
  organisationGroupsListAction,
  organisationStaffListAction,
} from "../../Redux/Action/OrganisationAction";

const getStaffRows = (payload) => {
  const data = payload?.data || payload || {};
  const staff = data?.staff || data?.rows || data?.docs || [];
  return Array.isArray(staff) ? staff : [];
};

const getStaffMeta = (payload, fallbackPage = 0, fallbackLimit = 10) => {
  const data = payload?.data || payload || {};
  const total = Number(data?.total || 0);
  const limit = Number(data?.limit || fallbackLimit || 10);

  return {
    total,
    page: Number.isFinite(Number(data?.page))
      ? Number(data?.page)
      : fallbackPage,
    limit,
    totalPages:
      Number(data?.totalPages) || Math.ceil(total / (limit || 10)) || 0,
  };
};

const getFullName = (staff) =>
  [staff?.title, staff?.firstName, staff?.surName].filter(Boolean).join(" ") ||
  "-";

const getSchoolName = (staff) =>
  staff?.school?.schoolName || staff?.schoolName || "-";

const formatDisplayDateTime = (value) => {
  if (!value) return "Never";

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    // hour: "2-digit",
    // minute: "2-digit",
  });
};

export default function OrganisationGroupsStaff() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const navigate = useNavigate();
  const organisationSchoolId =
    userData?.organisationGroupId?._id || userData?.schoolId || "";

  const [searchValue, setSearchValue] = useState("");
  const [selectedOrganisation, setSelectedOrganisation] = useState("");
  const [selectedAccountType, setSelectedAccountType] = useState("");
  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [tableMeta, setTableMeta] = useState({
    total: 0,
    page: 0,
    limit: 10,
    totalPages: 0,
  });
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [organisationOptions, setOrganisationOptions] = useState([]);

  useEffect(() => {
    if (!organisationSchoolId) return;
    dispatch(
      organisationGroupsListAction({
        groupId: organisationSchoolId,
        page: 0,
        limit: 100,
        search: "",
      }),
    ).then((res) => {
      const payload = res?.payload?.data;
      const orgs =
        payload?.organisations ||
        payload?.organisationsList ||
        payload?.rows ||
        payload?.data ||
        [];
      setOrganisationOptions(Array.isArray(orgs) ? orgs : []);
    });
  }, [dispatch, organisationSchoolId]);

  const filteredStaff = useMemo(() => {
    return tableData.filter(
      (item) =>
        (!selectedOrganisation ||
          (item?.school?._id || item?.schoolId?._id || item?.schoolId) ===
            selectedOrganisation) &&
        (!selectedAccountType || item?.accountType === selectedAccountType),
    );
  }, [selectedOrganisation, selectedAccountType, tableData]);

  const handleDetailClick = (data) => {
    navigate(`/organisations/staff-detail/${data?._id}/${data?.accountType}`);
  };

  const handleOrganisationSelect = (e) => {
    setSelectedOrganisation(e.target.value);
  };

  const handleAccountTypeSelect = (e) => {
    setSelectedAccountType(e.target.value);
  };

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === "asc";
    setSortBy(field);
    setSortOrder(isAsc ? "desc" : "asc");
    setPage(0);
  };

  const selectedOrganisationSchoolId = selectedOrganisation || "";

  useEffect(() => {
    const fetchStaff = async () => {
      setIsLoading(true);
      setLoadError(null);

      const response = await dispatch(
        organisationStaffListAction({
          page,
          limit: tableMeta.limit || 10,
          search: searchValue.trim(),
          schoolId: selectedOrganisationSchoolId,
          accountType: selectedAccountType,
          sortBy,
          sortOrder,
        }),
      );

      console.log("organisationStaffListAction response", response?.payload);

      if (response?.error) {
        setLoadError(response?.payload || response?.error || null);
        toast.error(
          response?.payload?.message || "Unable to load organisation staff.",
        );
        setTableData([]);
        setTableMeta({
          total: 0,
          page,
          limit: tableMeta.limit || 10,
          totalPages: 0,
        });
        setIsLoading(false);
        return;
      }

      setTableData(getStaffRows(response?.payload));
      setTableMeta(
        getStaffMeta(response?.payload, page, tableMeta.limit || 10),
      );
      setIsLoading(false);
    };

    fetchStaff();
  }, [
    dispatch,
    organisationSchoolId,
    page,
    searchValue,
    tableMeta.limit,
    selectedOrganisationSchoolId,
    selectedAccountType,
    sortBy,
    sortOrder,
  ]);

  const totalPages = tableMeta.totalPages || 0;
  const pageStart = tableMeta.total === 0 ? 0 : page * tableMeta.limit + 1;
  const pageEnd = Math.min((page + 1) * tableMeta.limit, tableMeta.total);

  return (
    <OrganisationLayout>
      <TopHeader setSearchValue={setSearchValue} groupData={[]} type="Class" />

      <div className="heading-top mt-5 mb-2">
        <p className="organization-crumb">Organisation Staff</p>
        <h2>All Staff</h2>
      </div>
      <Container fluid>
        <Row>
          <Col lg={4}>
            <SearchBar setSearchValue={setSearchValue} />
          </Col>
          <Col lg={3}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Default select example"
                className="cmn-select"
                onChange={handleOrganisationSelect}
                value={selectedOrganisation}
              >
                <option value="">All Organisations</option>
                {organisationOptions?.map((item) => (
                  <option
                    value={item?.organisationId}
                    key={item?.organisationId}
                  >
                    {item?.organisationName ||
                      item?.schoolId?.schoolName ||
                      item?.schoolName ||
                      "-"}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>
          <Col lg={3}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Account Type"
                className="cmn-select"
                onChange={handleAccountTypeSelect}
                value={selectedAccountType}
              >
                <option value="">All Account Types</option>
                <option value="Teacher">Teacher</option>
                <option value="School Admin">School Admin</option>
                {/* <option value="Organisation Group Member">Organisation Group Member</option> */}
              </Form.Select>
            </div>
          </Col>
        </Row>
      </Container>
      <Container fluid>
        <section className="organisation-groups-table-shell organisation-groups-table-shell-space">
          <div className="organisation-panel-heading">
            <div>
              <h3>Staff list</h3>
            </div>
          </div>
          <div className="organisation-table-responsive">
            <table>
              <thead>
                <tr>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "firstName" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("firstName")}
                    >
                      Staff Member
                    </span>
                  </th>
                  <th>From Organisation</th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "email" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("email")}
                    >
                      Email
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "department" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("department")}
                    >
                      Department
                    </span>
                  </th>
                  <th>Account Type</th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "createdAt" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("createdAt")}
                    >
                      Joined Peerscroller
                    </span>
                  </th>
                  <th>
                    <span
                      className={`cursor-pointer ${
                        sortBy === "lastLoggedIn" ? sortOrder : ""
                      }`}
                      onClick={() => handleSort("lastLoggedIn")}
                    >
                      Last Logged In
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={6} className="text-center py-5">
                      Loading staff...
                    </td>
                  </tr>
                )}
                {!isLoading && loadError && (
                  <tr>
                    <td colSpan={6} className="text-center py-5">
                      Unable to load organisation staff right now.
                    </td>
                  </tr>
                )}
                {!isLoading && !loadError && filteredStaff.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-5">
                      No staff found.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  !loadError &&
                  filteredStaff.map((row) => (
                    <tr key={row?._id || row?.email}>
                      <td>
                        <div
                          className="organisation-person-cell cursor-pointer"
                          onClick={() => handleDetailClick(row)}
                        >
                          <img
                            className="organisation-avatar-sm"
                            src={
                              row?.image ||
                              require("../../Assets/Images/girl.png")
                            }
                            alt={getFullName(row)}
                            height="42"
                            width="42"
                          />
                          <div className="organisation-name-cell">
                            <strong>{getFullName(row)}</strong>
                            <p>{getSchoolName(row)}</p>
                          </div>
                        </div>
                      </td>
                      <td>{getSchoolName(row)}</td>
                      <td>{row?.email || "-"}</td>
                      <td>{row?.department || "-"}</td>
                      <td>{row?.accountType || "-"}</td>
                      <td>{formatDisplayDateTime(row?.createdAt)}</td>
                      <td>{formatDisplayDateTime(row?.lastLoggedIn)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="organisation-pagination">
            <div className="organisation-pagination-meta">
              Showing {pageStart}-{pageEnd} of {tableMeta.total || 0} staff
            </div>
            <div className="organisation-pagination-actions">
              <button
                className="organisation-pagination-btn"
                onClick={() =>
                  setPage((prevState) => Math.max(prevState - 1, 0))
                }
                disabled={page === 0 || isLoading}
              >
                Previous
              </button>
              <span className="organisation-pagination-page">
                Page {page + 1}
                {totalPages > 0 ? ` of ${totalPages}` : ""}
              </span>
              <button
                className="organisation-pagination-btn"
                onClick={() => setPage((prevState) => prevState + 1)}
                disabled={
                  isLoading ||
                  filteredStaff.length === 0 ||
                  (totalPages > 0 && page + 1 >= totalPages)
                }
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </Container>
    </OrganisationLayout>
  );
}
